/**
 * FTR (Foundational Technical Review) module schema.
 *
 * Rewritten 2026-07-09 against AWS's current FTR guide. The FTR is no longer a
 * self-assessment checklist of controls: it is five prerequisites, then ONE
 * document (a SOC 2 Type II report *or* a WAFR report) uploaded as a PDF ≤ 3 MB
 * and validated by Amazon Bedrock in minutes. There is no CIS benchmark, no
 * control checklist and no architecture diagram. An approved FTR lasts two years.
 *
 * Backend has the parallel section/field skeleton in
 * `backend/app/domain/ftr_schema.py`; `backend/tests/test_schema_in_sync.py`
 * asserts the two stay in sync (including which fields are `required`).
 *
 * The shared technical-baseline sections (`companyProfileSchema.ts`) render
 * inside the FTR workspace too, but store on the company profile.
 */

import type { ChoiceOption, SectionDef } from './types'

/** Shared yes/no scale. */
const YES_NO_OPTIONS: ChoiceOption[] = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
  { value: 'Not applicable', label: 'Not applicable' },
]

/** AWS: "your solution must be linked to a SaaS, AMI, ML, or Container product
 *  (services-only solutions are not supported)". */
const PRODUCT_TYPE_OPTIONS: ChoiceOption[] = [
  { value: 'SaaS', label: 'SaaS' },
  { value: 'AMI', label: 'AMI' },
  { value: 'ML', label: 'Machine learning' },
  { value: 'Container', label: 'Container' },
  { value: 'Services only', label: 'Services only — not eligible' },
]

const PATHWAY_OPTIONS: ChoiceOption[] = [
  { value: 'WAFR', label: 'WAFR report' },
  { value: 'SOC 2 Type II', label: 'SOC 2 Type II report' },
  { value: 'Undecided', label: 'Undecided' },
]

/** AWS's FTR status values on the Validation tab. */
const SUBMISSION_STATUS_OPTIONS: ChoiceOption[] = [
  { value: 'Not submitted', label: 'Not submitted' },
  { value: 'Blocked', label: 'Blocked — prerequisites incomplete' },
  { value: 'Action required', label: 'Action required — validation did not pass' },
  { value: 'Approved', label: 'Approved' },
]

/** Company-owned baseline sections rendered inside the FTR workspace —
 *  mirrors `ftr_schema.SHARED_SECTIONS` (sync-tested). */
export const FTR_SHARED_SECTION_KEYS = [
  'architecture_baseline', 'security_posture', 'operations_practices',
]

export const FTR_SECTIONS: SectionDef[] = [
  {
    key: 'eligibility',
    number: 1,
    title: 'Eligibility prerequisites',
    description:
      'The five checks AWS runs before it will even accept an FTR request. All five must pass or the Request Validation button stays disabled.',
    help:
      "Partner Central shows these as a prerequisites table on the solution's **Validation** tab, each reading Complete, Incomplete, or Blocked (\"Blocked\" means an earlier prerequisite hasn't passed yet). Until all five read Complete, the FTR status itself is **Blocked** and the Request Validation button is disabled.\n\nNone of these are about the workload's engineering quality — they are about whether the solution is set up correctly in Partner Central and AWS Marketplace. They are also the slowest to fix, so start them early: **Partner Revenue Measurement has a 7-day data lag**.",
    fields: [
      {
        key: 'single_marketplace_product',
        label: 'Linked to exactly one AWS Marketplace product',
        hint:
          'The solution must be linked to exactly one Marketplace product — AWS says multi-product solutions are not eligible at this time.',
        help:
          "**Why it matters:** AWS validates one solution against one product. Its words: \"Solution linked to exactly one AWS Marketplace product — multi-product solutions are not eligible at this time.\"\n\n**A good answer:** Yes, once the solution in Partner Central points at a single Marketplace listing. If the solution bundles several products, split it before requesting FTR.\n\n**Example:** The \"Acme Storefront\" solution links only to the Acme Storefront SaaS listing; the separate Acme Analytics listing has its own solution record.",
        type: { kind: 'radio', options: YES_NO_OPTIONS },
        priority: 'required',
      },
      {
        key: 'no_non_marketplace_products',
        label: 'No non-Marketplace products in the solution',
        hint:
          'Remove any products that are not AWS Marketplace listings before submitting.',
        help:
          "**Why it matters:** AWS: \"No non-Marketplace products in solution — remove any non-Marketplace products before submitting.\" A stray non-Marketplace product silently blocks the request.\n\n**A good answer:** Yes, after auditing the solution's product list in Partner Central.",
        type: { kind: 'radio', options: YES_NO_OPTIONS },
        priority: 'required',
      },
      {
        key: 'product_type',
        label: 'Product type',
        hint:
          'The linked product must be SaaS, AMI, ML or Container. Services-only solutions cannot take an FTR — the Services FTR was discontinued.',
        help:
          "**Why it matters:** AWS: \"Software product type — your solution must be linked to a SaaS, AMI, ML, or Container product (services-only solutions are not supported).\"\n\n**Services-only solutions have no path here.** AWS has retired the Services FTR entirely: \"Services FTR won't be resumed.\" An existing Qualified Services badge runs to its expiration date and then lapses.\n\n**A good answer:** the product type of the single linked Marketplace listing.",
        type: { kind: 'radio', options: PRODUCT_TYPE_OPTIONS },
        priority: 'required',
      },
      {
        key: 'deployed_on_aws_badge',
        label: '“Deployed on AWS” badge on the listing',
        hint:
          'The Marketplace listing must carry the Deployed on AWS badge, i.e. the product is hosted on AWS infrastructure.',
        help:
          "**Why it matters:** AWS requires the listing to be \"hosted on AWS infrastructure\", shown by the **Deployed on AWS** badge. The badge reaches beyond FTR: since 1 January 2026 only SaaS products carrying it are eligible for AWS field incentives, including the SaaS Co-Sell Benefit. Passing FTR is itself the fastest route to the badge, so the two are mutually reinforcing.\n\n**A good answer:** Yes once the badge shows on the listing.",
        type: { kind: 'radio', options: YES_NO_OPTIONS },
        priority: 'required',
      },
      {
        key: 'prm_enabled',
        label: 'Partner Revenue Measurement (PRM) enabled',
        hint:
          'Opt in on the product’s Attributed Revenue tab in the Marketplace Management Portal. Allow 7 days for the status to flip.',
        help:
          "**Why it matters:** PRM is how AWS attributes customer workloads to your solution. AWS: \"It ensures that when you achieve Qualified Software status, you are also set up for revenue attribution.\" Enable it via **Resource Tagging**, a **User Agent string**, or **Marketplace Metering**, from *AWS Marketplace Management Portal → Attributed Revenue tab*.\n\n**Start this first.** AWS: \"There is a 7-day lag between enabling PRM and the status updating in the system.\" If the prerequisite still reads Incomplete, wait out the seven days, then return to the Validation tab and click Refresh — it is not a bug.\n\n**A good answer:** Yes, with the enablement date recorded below so the 7-day wait is visible.",
        type: { kind: 'radio', options: YES_NO_OPTIONS },
        priority: 'required',
      },
      {
        key: 'prm_enabled_date',
        label: 'PRM enablement date',
        hint:
          'When PRM was switched on. The prerequisite cannot read Complete until roughly 7 days after this date.',
        type: { kind: 'text', placeholder: 'YYYY-MM-DD' },
        priority: 'recommended',
      },
      {
        key: 'notes',
        label: 'Eligibility notes',
        hint: 'Anything about the prerequisites the questions above miss — e.g. an account-linking or migration step still outstanding.',
        type: { kind: 'textarea', rows: 3 },
      },
    ],
  },
  {
    key: 'pathway',
    number: 2,
    title: 'Submission pathway',
    description:
      'One document decides the FTR: a SOC 2 Type II audit report, or a WAFR report. They are peers — pick whichever you already have.',
    help:
      "AWS: \"SOC 2 Type II is a third-party audited compliance report — best if you already invest in SOC 2 certification for your customers. WAFR is a review against AWS Well-Architected best practices. **Either pathway works — choose based on what you have.**\" You can switch freely between them at renewal.\n\nThis is not a fast-track-versus-fallback choice: there is no checklist to fall back to. Whichever document you upload, Amazon Bedrock validates it against that pathway's checks and returns a per-control pass/fail in minutes, with no limit on resubmissions.\n\n**On the WAFR pathway the checks are read from the review itself** — report under 12 months old, zero active high-risk issues in Security, Operational Excellence and Reliability, and the workload named for the solution — so they are shown on the Submission station rather than re-asked here. The SOC 2 questions below mirror AWS's five SOC 2 checks.",
    fields: [
      {
        key: 'submission_pathway',
        label: 'Chosen pathway',
        hint:
          'Which document will be uploaded. Choose WAFR if a Well-Architected review is already under way; SOC 2 Type II if the audit already exists.',
        help:
          "**Why it matters:** it decides which validation checks AWS runs and what has to be true before submitting. There is no third option and no checklist alternative.\n\n**A good answer:** WAFR when a clean review is within reach (the review is free, and it also feeds the Well-Architected Partner Program); SOC 2 Type II when the audit is already in hand and covers AWS and this solution.",
        type: { kind: 'radio', options: PATHWAY_OPTIONS },
        priority: 'required',
      },
      {
        key: 'soc2_audit_period_current',
        label: 'SOC 2 — report issued within the last year',
        hint:
          'SOC 2 pathway only. AWS: “your report issue date must be within 1 year of submission”.',
        help:
          "**Why it matters:** one of AWS's five SOC 2 checks. A report older than a year fails validation outright.\n\n**Note on renewal:** SOC 2 has **no grace period** — submit a new valid report before the FTR expires.",
        type: { kind: 'radio', options: YES_NO_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'soc2_unqualified_opinion',
        label: 'SOC 2 — unqualified auditor opinion',
        hint: 'SOC 2 pathway only. The auditor’s opinion must be unqualified (clean).',
        type: { kind: 'radio', options: YES_NO_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'soc2_aws_in_scope',
        label: 'SOC 2 — AWS explicitly in scope',
        hint: 'SOC 2 pathway only. AWS infrastructure must be explicitly listed as in-scope for the audit.',
        type: { kind: 'radio', options: YES_NO_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'soc2_solution_in_scope',
        label: 'SOC 2 — this solution identified in the report',
        hint: 'SOC 2 pathway only. The specific solution being validated must be identified by name.',
        type: { kind: 'radio', options: YES_NO_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'soc2_trust_services_criteria',
        label: 'SOC 2 — Security and Availability criteria both covered',
        hint: 'SOC 2 pathway only. Both Trust Services Criteria must be included; Security alone is not enough.',
        type: { kind: 'radio', options: YES_NO_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'notes',
        label: 'Pathway notes',
        hint: 'Why this pathway, and anything outstanding before the document is ready to upload.',
        type: { kind: 'textarea', rows: 3 },
      },
    ],
  },
  {
    key: 'submission',
    number: 3,
    title: 'Submission record',
    description:
      'What AWS decided, recorded by hand. FTR submission has no public API and no status API — the result lives only on the Partner Central Validation tab.',
    help:
      "The request is made in the AWS Partner Central portal: upload the PDF (**maximum 3 MB**) and click Submit for Validation. Automated validation begins immediately and results come back in minutes — \"there is no manual review queue\". If it fails, a Validation Feedback section names each failing control with remediation steps, and the button becomes **Resubmit**. There is **no limit on resubmissions**.\n\nBecause there is **no public API**, nothing pulls the status back automatically — record it here so the rest of the platform can see it.\n\n**An approved FTR is valid for two years** from the approval date. Partner Central notifies at 90, 60 and 30 days before expiry, and the submit button re-opens 90 days out. On the WAFR pathway the report must be under 12 months old *at the moment of renewal*, so a review cut today cannot carry a renewal two years from now.",
    fields: [
      {
        key: 'submission_status',
        label: 'FTR status',
        hint:
          'The status shown on the Validation tab: Blocked (prerequisites incomplete), Action required (validation did not pass), or Approved.',
        help:
          "**Why it matters:** the FTR result lives only on the Partner Central Validation tab; this is the platform's copy.\n\n**Blocked** means the five prerequisites aren't all Complete — nothing has been validated yet. **Action required** means a document was validated and did not pass; read the Validation Feedback, fix, resubmit. **Approved** sets an expiration date two years out and immediately unlocks the Qualified Software badge, funding eligibility and co-sell readiness.\n\nIf you believe a result is wrong, AWS Partner Support can have a PSA review the submission.",
        type: { kind: 'radio', options: SUBMISSION_STATUS_OPTIONS },
        priority: 'recommended',
      },
      {
        key: 'submitted_date',
        label: 'Submitted on',
        hint: 'The date the document was uploaded for validation.',
        type: { kind: 'text', placeholder: 'YYYY-MM-DD' },
        priority: 'recommended',
      },
      {
        key: 'approval_date',
        label: 'Approved on',
        hint: 'The approval date. Expiry is exactly two years from this date.',
        type: { kind: 'text', placeholder: 'YYYY-MM-DD' },
        priority: 'recommended',
      },
      {
        key: 'expiration_date',
        label: 'Expires on',
        hint: 'Two years after approval. Renewal notices arrive at 90, 60 and 30 days; the submit button opens 90 days out.',
        type: { kind: 'text', placeholder: 'YYYY-MM-DD' },
        priority: 'recommended',
      },
      {
        key: 'validation_feedback',
        label: 'Validation feedback',
        hint:
          'When validation fails, AWS names each failing control with an explanation and remediation steps. Paste them here with what was done about each.',
        help:
          "**Why it matters:** the feedback is AI-generated and control-specific — it tells you exactly what to fix. Keeping it here makes the resubmission auditable, and makes a repeated failure obvious.\n\n**A good answer:** each failing control, what it said, and the change that answered it.\n\n**Example:** \"No High-Risk Issues in Reliability pillar — failed. REL 9 (backups) had an active HRI. Enabled AWS Backup for the RDS cluster, remediated in the WA Tool, cut a new milestone, resubmitted.\"",
        type: { kind: 'textarea', rows: 4, placeholder: 'Failing control → what it said → what we changed…' },
      },
      {
        key: 'notes',
        label: 'Submission notes',
        hint: 'Anything else about the submission — who uploaded it, support cases raised, PSA reviews requested.',
        type: { kind: 'textarea', rows: 3 },
      },
    ],
  },
]
